/**
 * OrderStatusSelectionModal Component
 * Allows user to select order statuses after drop completion
 */

import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Select,
  Button,
  Space,
  Typography,
  Alert,
  List,
  Tag,
} from "antd";
import { CheckCircleOutlined } from "@ant-design/icons";
import { logError, logInfo } from "../../../shared/utils/logger.js";

const { Text } = Typography;
const { Option } = Select;

const ORDER_STATUSES = [
  { value: "in_progress", label: "In Progress" },
  { value: "arrived_at_storage", label: "Arrived at Storage" },
  { value: "in_transit_from_storage", label: "In Transit from Storage" },
  { value: "completed", label: "Completed" },
];

/**
 * Get suggested status based on computedStatus
 * @param {string} computedStatus - Computed status from drops
 * @param {string} currentStatus - Current order status
 * @returns {string} Suggested status
 */
const getSuggestedStatus = (computedStatus, currentStatus) => {
  if (computedStatus === "delivered") {
    return "completed";
  }
  if (computedStatus === "partially_delivered") {
    return "in_progress";
  }
  if (computedStatus === "in_progress") {
    return "in_progress";
  }
  // Default to current status if no match
  return currentStatus || "in_progress";
};

const OrderStatusSelectionModal = ({
  open,
  orders = [],
  onConfirm,
  onCancel,
  loading = false,
}) => {
  const [form] = Form.useForm();
  const [selectedStatuses, setSelectedStatuses] = useState({});
  const [originalStatuses, setOriginalStatuses] = useState({}); // Track original statuses

  useEffect(() => {
    if (open && orders && orders.length > 0) {
      // Pre-populate with suggested statuses based on computedStatus
      const initialStatuses = {};
      const originalStatusesMap = {};
      
      orders.forEach((order) => {
        const suggested = getSuggestedStatus(
          order.computedStatus,
          order.status
        );
        initialStatuses[order.id] = suggested;
        originalStatusesMap[order.id] = order.status || "pending"; // Store original status
      });
      
      setSelectedStatuses(initialStatuses);
      setOriginalStatuses(originalStatusesMap); // Store original statuses
      form.setFieldsValue(initialStatuses);

      logInfo("OrderStatusSelectionModal opened", {
        ordersCount: orders.length,
        initialStatuses,
        originalStatuses: originalStatusesMap,
      });
    }
  }, [open, orders, form]);

  const handleSubmit = async () => {
    try {
      const values = form.getFieldsValue();
      
      // Filter to only include orders with changed statuses
      const changedStatuses = {};
      for (const [orderId, newStatus] of Object.entries(values)) {
        const originalStatus = originalStatuses[orderId];
        if (newStatus && newStatus !== originalStatus) {
          changedStatuses[orderId] = newStatus;
        }
      }

      if (Object.keys(changedStatuses).length === 0) {
        logInfo("OrderStatusSelectionModal: No status changes detected", {
          ordersCount: orders.length,
        });
        // Close modal without updates if no changes
        if (onCancel) {
          onCancel();
        }
        return;
      }

      logInfo("OrderStatusSelectionModal: Submitting status updates", {
        changedStatuses,
        totalOrders: orders.length,
        changedCount: Object.keys(changedStatuses).length,
      });

      if (onConfirm) {
        await onConfirm(changedStatuses); // Only pass changed statuses
      }
    } catch (error) {
      logError("OrderStatusSelectionModal: Error submitting status updates", {
        error: error.message,
        stack: error.stack,
      });
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setSelectedStatuses({});
    setOriginalStatuses({});
    if (onCancel) {
      onCancel();
    }
  };

  if (!orders || orders.length === 0) {
    return null;
  }

  return (
    <Modal
      title={
        <Space>
          <CheckCircleOutlined style={{ color: "#1890ff" }} />
          <Text strong>Update Order Statuses</Text>
        </Space>
      }
      open={open}
      onCancel={handleCancel}
      footer={null}
      width={700}
      destroyOnHidden
    >
      <Alert
        message="Drop completed successfully"
        description="Please select the appropriate status for each affected order. The system has calculated suggested statuses based on drop completion."
        type="success"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Form form={form} layout="vertical">
        <List
          dataSource={orders}
          renderItem={(order) => (
            <List.Item>
              <Space direction="vertical" style={{ width: "100%" }}>
                <Space>
                  <Text strong>{order.id}</Text>
                  <Tag color="blue">
                    Computed: {order.computedStatus || "N/A"}
                  </Tag>
                  <Tag color="default">Current: {order.status || "pending"}</Tag>
                </Space>
                <Form.Item
                  name={order.id}
                  label={`Customer: ${order.customer || "N/A"}`}
                  style={{ marginBottom: 0 }}
                >
                  <Select
                    placeholder="Select order status"
                    onChange={(value) => {
                      setSelectedStatuses((prev) => ({
                        ...prev,
                        [order.id]: value,
                      }));
                    }}
                  >
                    {ORDER_STATUSES.map((status) => (
                      <Option key={status.value} value={status.value}>
                        {status.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Space>
            </List.Item>
          )}
        />

        <Form.Item style={{ marginTop: 24, marginBottom: 0 }}>
          <Space style={{ width: "100%", justifyContent: "flex-end" }}>
            <Button onClick={handleCancel}>Skip</Button>
            <Button
              type="primary"
              onClick={handleSubmit}
              loading={loading}
              icon={<CheckCircleOutlined />}
            >
              Update Order Statuses
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default OrderStatusSelectionModal;

